// As "rodas" do escritório: o que os ociosos fazem juntos (TV, videogame, fliperama, pingue-pongue,
// papo na copa, conversa no corredor, jokenpô e espelho do banheiro).
//
// Puro, sem DOM e sem relógio próprio: o instante entra por parâmetro e toda escolha sai de hash,
// então o mesmo instante dá sempre o mesmo resultado (e os testes não dependem da hora).
//
//   traço (14, pelo id do agente) → peso de cada roda → quem senta em qual mesa
//   mesa + semente + tempo        → placar, gestos, programa da TV, vez de falar
//
// Quem desenha e anda é src/web/world/rodas.ts; aqui só moram as regras.

import { stableHash } from './hash.js';

/** Os 14 traços de personalidade. */
export const TRACOS = ['falante', 'competitivo', 'caseiro', 'gamer', 'cinefilo', 'fofoqueiro', 'atleta', 'vaidoso', 'cafeinado', 'brincalhao', 'sossegado', 'retro', 'sociavel', 'curioso'] as const;
export type Traco = (typeof TRACOS)[number];

/** As rodas. A ordem é a das colunas de PESOS. */
export const RODAS = ['tv', 'videogame', 'fliperama', 'pingpong', 'papo', 'corredor', 'jokenpo', 'espelho'] as const;
export type RodaKind = (typeof RODAS)[number];

/** Quanto cada traço gosta de cada roda (0 = tanto faz, 4 = não vive sem). Colunas: ver RODAS. */
const PESOS: Readonly<Record<Traco, readonly number[]>> = {
  //               tv vg fl pp papo cor jk esp
  falante: [1, 1, 0, 1, 4, 4, 2, 0],
  competitivo: [0, 3, 3, 4, 0, 0, 4, 0],
  caseiro: [3, 1, 0, 0, 2, 0, 1, 0],
  gamer: [1, 4, 4, 1, 0, 0, 1, 0],
  cinefilo: [4, 1, 0, 0, 2, 1, 0, 0],
  fofoqueiro: [1, 0, 0, 0, 4, 4, 1, 1],
  atleta: [2, 0, 0, 4, 1, 1, 1, 1],
  vaidoso: [0, 0, 0, 0, 1, 1, 1, 4],
  cafeinado: [1, 0, 0, 1, 4, 2, 1, 0],
  brincalhao: [1, 2, 2, 2, 2, 1, 4, 1],
  sossegado: [3, 0, 0, 0, 2, 1, 0, 0],
  retro: [2, 2, 4, 1, 1, 0, 2, 0],
  sociavel: [1, 2, 0, 2, 3, 4, 2, 0],
  curioso: [2, 2, 2, 1, 2, 2, 1, 2],
};

/** Pares de traços que se dão bem (simétrico): sentam juntos de preferência. */
const AFINS: ReadonlyArray<readonly [Traco, Traco]> = [
  ['falante', 'fofoqueiro'],
  ['competitivo', 'atleta'],
  ['gamer', 'retro'],
  ['cinefilo', 'caseiro'],
  ['cafeinado', 'falante'],
  ['brincalhao', 'sociavel'],
  ['vaidoso', 'fofoqueiro'],
  ['sossegado', 'caseiro'],
  ['competitivo', 'gamer'],
];

/** O traço de um agente: sempre o mesmo para o mesmo id. */
export function tracoDe(agentId: string): Traco {
  return TRACOS[stableHash(`${agentId}|traco`) % TRACOS.length]!;
}

/** Peso (0–4) que o traço dá à roda. */
export function pesoDaRoda(traco: Traco, roda: RodaKind): number {
  return PESOS[traco][RODAS.indexOf(roda)] ?? 0;
}

function afinidade(a: Traco, b: Traco): number {
  if (a === b) return 0.6;
  return AFINS.some(([x, y]) => (x === a && y === b) || (x === b && y === a)) ? 1 : 0;
}

/** Uma mesa (lugar da roda): quantos assentos tem e quantos precisa para a roda começar. */
export interface Mesa {
  id: string;
  kind: RodaKind;
  seats: number;
  min: number;
}

/** Quem pode entrar numa roda: um ocioso. */
export interface Candidato {
  id: string;
  traco: Traco;
  /** Ids dos amigos (vida social): sentam juntos de preferência. */
  friends?: ReadonlySet<string>;
}

/** Uma roda formada: a mesa, a semente (para as simulações) e quem está nela, na ordem dos assentos. */
export interface Sessao {
  mesa: Mesa;
  seed: string;
  members: string[];
}

/** Quanto tempo a roda dura (60 a 120 s, por hash da semente). */
export function duracaoDaRoda(seed: string): number {
  return 60_000 + (stableHash(`${seed}|dur`) % 7) * 10_000;
}

const SEED_MIN = 1.5;
/** Roda de uma pessoa só (TV, fliperama, espelho) exige mais gosto: senão metade do prédio vai para o espelho. */
const SEED_SOLO_MIN = 2.4;
const PARTNER_MIN = 1;

/** Sorteio estável de 0 a `max` (exclusive, em décimos) por hash. */
function ruido(...parts: Array<string | number>): number {
  return (stableHash(parts.join('|')) % 140) / 100;
}

/**
 * Forma as rodas deste instante: as mesas entram em ordem sorteada (pela janela `slot`); cada uma
 * escolhe como semente quem mais gosta dela e depois os parceiros de melhor afinidade. Mesa que
 * não junta o mínimo de gente fica vazia. Quem sobra não entra em roda (fica no descanso comum).
 */
export function planejaRodas(mesas: readonly Mesa[], candidatos: readonly Candidato[], slot: number): Sessao[] {
  const out: Sessao[] = [];
  const order = [...mesas].sort((a, b) => stableHash(`${a.id}|${slot}`) - stableHash(`${b.id}|${slot}`) || (a.id < b.id ? -1 : 1));
  let rest = [...candidatos].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (const mesa of order) {
    if (rest.length < mesa.min) continue;
    const gosto = (c: Candidato) => pesoDaRoda(c.traco, mesa.kind) + ruido(c.id, mesa.id, slot);
    const ranked = [...rest].sort((a, b) => gosto(b) - gosto(a) || (a.id < b.id ? -1 : 1));
    const seed = ranked[0]!;
    if (gosto(seed) < (mesa.min === 1 ? SEED_SOLO_MIN : SEED_MIN)) continue;
    const size = Math.min(mesa.seats, mesa.min + (stableHash(`${seed.id}|${mesa.id}|${slot}|n`) % (mesa.seats - mesa.min + 1)));
    const nota = (c: Candidato) => gosto(c) + afinidade(seed.traco, c.traco) + (seed.friends?.has(c.id) || c.friends?.has(seed.id) ? 1.2 : 0);
    const partners = ranked
      .slice(1)
      .filter((c) => gosto(c) >= PARTNER_MIN)
      .sort((a, b) => nota(b) - nota(a) || (a.id < b.id ? -1 : 1))
      .slice(0, size - 1);
    const members = [seed, ...partners].map((c) => c.id);
    if (members.length < mesa.min) continue;
    out.push({ mesa, seed: `${mesa.id}|${slot}|${seed.id}`, members });
    const taken = new Set(members);
    rest = rest.filter((c) => !taken.has(c.id));
  }
  return out;
}

// ------------------------------------------------------------------ pingue-pongue

export const PING_POINT_MS = 1500;
export const PING_WIN = 11;
const PING_PAUSE_MS = 4000;

export interface PingEstado {
  score: [number, number];
  /** Partida terminada (mostra o vencedor até a próxima). */
  over: boolean;
  winner: 0 | 1;
  /** Pontos já jogados na partida e quando ela começou (ms desde o início da roda). */
  points: number;
  matchStart: number;
}

/** Placar do pingue-pongue `elapsed` ms depois de a roda começar: pontos de 1,5 s, partida até 11. */
export function pingPong(seed: string, elapsed: number): PingEstado {
  let start = 0;
  for (let m = 0; m < 200; m++) {
    const score: [number, number] = [0, 0];
    let p = 0;
    while (score[0] < PING_WIN && score[1] < PING_WIN) {
      score[stableHash(`${seed}|pp|${m}|${p}`) % 2]! += 1;
      p++;
    }
    const winner: 0 | 1 = score[0] > score[1] ? 0 : 1;
    const play = p * PING_POINT_MS;
    const at = elapsed - start;
    if (at < play) {
      const done = Math.floor(at / PING_POINT_MS);
      const partial: [number, number] = [0, 0];
      for (let i = 0; i < done; i++) partial[stableHash(`${seed}|pp|${m}|${i}`) % 2]! += 1;
      return { score: partial, over: false, winner, points: done, matchStart: start };
    }
    if (at < play + PING_PAUSE_MS) return { score, over: true, winner, points: p, matchStart: start };
    start += play + PING_PAUSE_MS;
  }
  return { score: [0, 0], over: false, winner: 0, points: 0, matchStart: 0 };
}

// ------------------------------------------------------------------ videogame (melhor de 3)

export const GAME_MS = 12_000;
const GAME_PAUSE_MS = 5000;

export interface MelhorDe3 {
  score: [number, number];
  /** Fim da série: alguém fez 2. */
  over: boolean;
  winner: 0 | 1;
  /** Número da partida em andamento (1 a 3). */
  game: number;
}

/** Melhor de 3 no videogame: cada partida dura 12 s, a série vale até 2 vitórias e recomeça depois. */
export function melhorDe3(seed: string, elapsed: number): MelhorDe3 {
  let start = 0;
  for (let m = 0; m < 200; m++) {
    const score: [number, number] = [0, 0];
    let g = 0;
    while (score[0] < 2 && score[1] < 2) {
      score[stableHash(`${seed}|vg|${m}|${g}`) % 2]! += 1;
      g++;
    }
    const winner: 0 | 1 = score[0] > score[1] ? 0 : 1;
    const play = g * GAME_MS;
    const at = elapsed - start;
    if (at < play) {
      const done = Math.floor(at / GAME_MS);
      const partial: [number, number] = [0, 0];
      for (let i = 0; i < done; i++) partial[stableHash(`${seed}|vg|${m}|${i}`) % 2]! += 1;
      return { score: partial, over: false, winner, game: done + 1 };
    }
    if (at < play + GAME_PAUSE_MS) return { score, over: true, winner, game: g };
    start += play + GAME_PAUSE_MS;
  }
  return { score: [0, 0], over: false, winner: 0, game: 1 };
}

// ------------------------------------------------------------------ jokenpô

/** 0 pedra, 1 papel, 2 tesoura. */
export type Gesto = 0 | 1 | 2;
export const GESTOS: readonly string[] = ['✊', '✋', '✌️'];
export const JOKENPO_ROUND_MS = 3200;
const JOKENPO_THROW_MS = 1200;
const JOKENPO_SHOW_MS = 2000;

export interface JokenpoEstado {
  round: number;
  /** pronto: punhos balançando; jogou: gestos na mão; resultado: quem ganhou recebe a moedinha. */
  fase: 'pronto' | 'jogou' | 'resultado';
  gestos: [Gesto, Gesto];
  /** Quem ganhou a rodada (-1 = empate). */
  winner: -1 | 0 | 1;
  /** Rodadas vencidas por cada um até a rodada anterior. */
  score: [number, number];
  /** 0 a 1 dentro da fase de resultado (a moedinha voa). */
  voo: number;
}

function gestosDa(seed: string, round: number): [Gesto, Gesto] {
  return [(stableHash(`${seed}|jk|${round}|0`) % 3) as Gesto, (stableHash(`${seed}|jk|${round}|1`) % 3) as Gesto];
}

/** Quem ganha: papel > pedra, tesoura > papel, pedra > tesoura. */
export function vencedorDoJokenpo(a: Gesto, b: Gesto): -1 | 0 | 1 {
  if (a === b) return -1;
  return (a - b + 3) % 3 === 1 ? 0 : 1;
}

export function jokenpo(seed: string, elapsed: number): JokenpoEstado {
  const round = Math.max(0, Math.floor(elapsed / JOKENPO_ROUND_MS));
  const within = elapsed - round * JOKENPO_ROUND_MS;
  const gestos = gestosDa(seed, round);
  const score: [number, number] = [0, 0];
  for (let r = 0; r < round; r++) {
    const w = vencedorDoJokenpo(...gestosDa(seed, r));
    if (w === 0) score[0] += 1;
    else if (w === 1) score[1] += 1;
  }
  const fase = within < JOKENPO_THROW_MS ? 'pronto' : within < JOKENPO_SHOW_MS ? 'jogou' : 'resultado';
  const voo = fase === 'resultado' ? Math.min(1, (within - JOKENPO_SHOW_MS) / (JOKENPO_ROUND_MS - JOKENPO_SHOW_MS)) : 0;
  return { round, fase, gestos, winner: vencedorDoJokenpo(...gestos), score, voo };
}

// ------------------------------------------------------------------ TV

export const PROGRAMAS = ['futebol', 'novela', 'desenho'] as const;
export type Programa = (typeof PROGRAMAS)[number];
/** A programação muda a cada 5 min, igual para todos os andares da mesma semente. */
export const PROGRAMA_MS = 5 * 60_000;

/** O que está passando na TV da mesa `mesaId` na hora `now` (epoch ms). */
export function programaDaTv(mesaId: string, now: number): Programa {
  return PROGRAMAS[stableHash(`${mesaId}|tv|${Math.floor(now / PROGRAMA_MS)}`) % PROGRAMAS.length]!;
}

/** Um lance da TV (gol, susto, piada) dura 2,5 s e acontece num ponto de cada janela de 20 s. */
export function lanceDaTv(seed: string, elapsed: number): boolean {
  const slot = Math.floor(elapsed / 20_000);
  const at = stableHash(`${seed}|lance|${slot}`) % 17_000;
  const into = elapsed - slot * 20_000;
  return into >= at && into < at + 2500;
}

// ------------------------------------------------------------------ fliperama

/** Pontos do fliperama: sobem a cada segundo, cada um do seu jeito. */
export function pontosFliperama(seed: string, elapsed: number): number {
  const rate = 90 + (stableHash(`${seed}|fl`) % 90);
  return Math.floor(Math.max(0, elapsed) / 1000) * rate;
}

// ------------------------------------------------------------------ papo e fofoca

/** Cada um fala por 4,5 s, na vez; o balão fica no ar 3,5 s. */
export const FALA_MS = 4500;
const FALA_SHOW_MS = 3500;

export interface Vez {
  /** Quem fala (índice em members). */
  speaker: number;
  /** Número da fala (para sortear a frase e o colega citado). */
  turn: number;
  /** O balão está visível agora. */
  visivel: boolean;
}

export function vezDeFalar(seed: string, members: number, elapsed: number): Vez {
  const turn = Math.max(0, Math.floor(elapsed / FALA_MS));
  const speaker = (turn + (stableHash(`${seed}|vez`) % Math.max(1, members))) % Math.max(1, members);
  return { speaker, turn, visivel: elapsed - turn * FALA_MS < FALA_SHOW_MS };
}

/** Sorteio entre `n` opções para a fala `turn` (frase da fofoca, colega citado, emoji). */
export function sorteio(seed: string, turn: number, n: number, salt = ''): number {
  return n > 0 ? stableHash(`${seed}|s|${turn}|${salt}`) % n : 0;
}
